<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useErrorOverlay } from "@/store/overlays/error-overlay.ts";

const { t } = useI18n();
const store = useErrorOverlay();
const error_message = computed((): string => {
  const error_category = store.error_category;
  console.log(`[error-overlay]: got error category ${error_category}`);
  if (error_category === "UNAUTHORIZED") {
    return (
      `${t("unauthorized_station")}\n` +
      `${t("login_logout")}\n` +
      `${t("apologize")}`
    );
  }
  if (error_category !== null) {
    const err = error_category.toLowerCase();
    if (["timeout", "bad_coupon", "invalid_coupon"].includes(err)) {
      return t(err);
    }
  }
  return (
    `${t("something_went_wrong")}\n` +
    `${t("apologize")}\n` +
    `(status code ${store.status_code})`
  );
});

function refresh() {
  window.location.reload();
}
</script>

<template>
  <v-overlay
    v-model="store.is_visible"
    no-click-animation
    absolute
    persistent
    z-index="920"
    class="align-center justify-center"
    style="background-color: rgba(232, 62, 54, 0.95)"
  >
    <v-container fluid>
      <v-col class="align-center justify-center text-wrap">
        <div
          class="text-center text-white"
          style="white-space: pre-line;"
        >
          {{ error_message }}
        </div>
        <v-row class="justify-center my-2">
          <v-btn class="mt-4" color="success" elevation="0" @click="refresh">
            <v-icon class="mr-1" small>mdi-refresh</v-icon>
            <span>{{ t("refreshPage") }}</span>
          </v-btn>
        </v-row>
      </v-col>
    </v-container>
  </v-overlay>
</template>

<i18n lang="yaml">
en:
  "something_went_wrong": "Something went wrong..."
  "apologize": "We apologize for the inconvenience."
  "unauthorized_station": "You're not authorized to charge on this station."
  "login_logout": "Try to logout and login again if you think this is a bug."
  "timeout": "Cannot reach our servers:\ncheck your internet connection"
  "refreshPage": "Refresh"
it:
  "something_went_wrong": "C'è stato un errore..."
  "apologize": "Ci spiace molto per l'inconveniente."
  "unauthorized_station": "Non sei autorizzato a caricare su questa colonnina"
  "login_logout": "Prova a effettuare un logout e successivamente un login se pensi che si tratti di un bug."
  "timeout": "Impossibile contattare i server:\ncontrolla la tua connessione ad internet"
  "refreshPage": "Ricarica"
fr:
  "something_went_wrong": "Une erreur est survenue..."
  "apologize": "Veuillez nous excuser pour la gêne occasionnée."
  "unauthorized_station": "Vous n'êtes pas autorisé à utiliser cette station."
  "login_logout": "Essayez de vous déconnecter/reconnecter si vous pensez que vous devriez y avoir accès."
  "timeout": "Ne peut pas atteindre nos serveurs,\nvérifiez votre connexion internet"
  "refreshPage": "Rafraîchir la page"
de:
  "something_went_wrong": "Das ist ein Fehler..."
  "apologize": "Wir entschuldigen uns sehr für die Unannehmlichkeiten."
  "unauthorized_station": "Sie sind nicht berechtigt, in diese Spalte hochzuladen"
  "login_logout": "Versuchen Sie, sich abzumelden und dann wieder anzumelden, wenn Sie der Meinung sind, dass dies ein Fehler ist."
  "timeout": "Server können nicht kontaktiert werden\nPrüfe deine Internetverbindung"
  "refreshPage": "Aufladen"
nl:
  "something_went_wrong": "Er is iets fout gegaan..."
  "apologize": "Wij verontschuldigen ons voor het ongemak."
  "unauthorized_station": "U mag dit station niet gebruiken."
  "login_logout": "Probeer af te melden/opnieuw aan te melden als u denkt dat u er toegang toe zou moeten hebben."
  "timeout": "Kan onze servers niet bereiken,\ncontroleer uw internetverbinding"
  "refreshPage": "De pagina vernieuwen"
es:
  "something_went_wrong": "Algo ha ido mal..."
  "apologize": "Nos disculpamos por las molestias ocasionadas."
  "unauthorized_station": "No está autorizado a cargar en esta estación."
  "login_logout": "Intente cerrar sesión y volver a iniciarla si cree que esto es un error."
  "timeout": "No se pueden contactar nuestros servidores:\ncomprueba tu conexión a Internet"
  "refreshPage": "Actualizar"
</i18n>

<style scoped lang="scss">
</style>

<!-- vim: softtabstop=2:shiftwidth=2
-->
